import { useState, useEffect } from 'react';
import { ref, onValue, get } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { database, auth } from '../../shared/firebase';

/**
 * Hook to get the total unread message count and pending request count
 * Use this in any component that needs to show the notification badge
 */
export function useUnreadCount() {
  const [user, setUser] = useState(null);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (!currentUser) {
        setUnreadMessages(0);
        setPendingRequests(0);
        setLoading(false);
      }
    });
    return () => unsubAuth();
  }, []);

  useEffect(() => {
    if (!user) return;

    // Get blocked users first
    const loadCounts = async () => {
      // Load blocks and deleted conversations
      const blocksSnap = await get(ref(database, `vjb_blocks/${user.uid}`));
      const blockedUsers = blocksSnap.val() || {};
      
      const deletedSnap = await get(ref(database, `vjb_deleted_conversations/${user.uid}`));
      const deletedConversations = deletedSnap.val() || {};

      // Listen to contact requests
      const requestsRef = ref(database, 'vjb_contact_requests');
      const unsubRequests = onValue(requestsRef, async (snapshot) => {
        const data = snapshot.val();
        if (!data) {
          setUnreadMessages(0);
          setPendingRequests(0);
          setLoading(false);
          return;
        }

        const requests = Object.entries(data).map(([id, req]) => ({ id, ...req }));
        
        // Count pending requests (received, not blocked)
        const pending = requests.filter(r => 
          r.status === 'pending' && 
          r.toUid === user.uid &&
          !blockedUsers[r.fromUid]
        );
        setPendingRequests(pending.length);

        // Get accepted conversations (not deleted, not blocked)
        const conversations = requests.filter(r => {
          const otherUid = r.fromUid === user.uid ? r.toUid : r.fromUid;
          return r.status === 'accepted' && 
            (r.fromUid === user.uid || r.toUid === user.uid) &&
            !deletedConversations[r.id] &&
            !blockedUsers[otherUid];
        });

        // Count unread messages in each conversation
        let totalUnread = 0;
        for (const convo of conversations) {
          const messagesSnap = await get(ref(database, `vjb_messages/${convo.id}`));
          if (messagesSnap.exists()) {
            const messages = Object.values(messagesSnap.val());
            const unread = messages.filter(m => m.fromUid !== user.uid && !m.readAt).length;
            totalUnread += unread;
          }
        }
        
        setUnreadMessages(totalUnread);
        setLoading(false);
      });

      return () => unsubRequests();
    };

    loadCounts();
  }, [user]);

  return {
    unreadMessages,
    pendingRequests,
    total: unreadMessages + pendingRequests,
    loading,
    isLoggedIn: !!user
  };
}
